import { flexRender, type Cell } from '@tanstack/react-table';
import { useLayoutEffect, useState } from 'react';

import { TableCell } from '@/components/Table';
import { TABLE_CELL_HEIGHT_CLASS, TABLE_CELL_PADDING_CLASS } from '@/components/Table/TableCell';
import { cn } from '@/lib/utils';

import {
  getColumnTextAlignmentClassName,
  getPinnedCellClassName,
  getPinnedCellStyles,
  type PinnedOffsets,
} from './dataTableCellStyles';
import { type DataTableColumnDef, type DataTableSize } from './types';

type DataTableTableBodyCellProps<TData extends object> = {
  cell: Cell<TData, unknown>;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  sortedColumnId?: string;
  rowExpanded?: boolean;
  pinnedOffsets: PinnedOffsets;
  animateVisibility?: boolean;
  isVisible?: boolean;
  animationDurationMs?: number;
  size?: DataTableSize;
  // When true, the cell mounts already "entered" and skips the first-paint closed → open
  // flip. Used for cells present in the table's initial render so they don't animate on
  // page load the way dynamically-added sub-rows do.
  suppressEnterAnimation?: boolean;
};

const DataTableTableBodyCell = <TData extends object>({
  cell,
  canScrollLeft,
  canScrollRight,
  sortedColumnId,
  rowExpanded = false,
  pinnedOffsets,
  animateVisibility = false,
  isVisible = true,
  animationDurationMs = 200,
  size = 'large',
  suppressEnterAnimation = false,
}: DataTableTableBodyCellProps<TData>) => {
  const isSortedColumn = sortedColumnId === cell.column.id;
  const columnDefinition = cell.column.columnDef as DataTableColumnDef<TData>;
  const customCellProps = columnDefinition.cellProps?.(cell.getContext());
  const {
    className: customClassName,
    style: customStyle,
    ...restCustomCellProps
  } = customCellProps ?? {};
  const content = flexRender(cell.column.columnDef.cell, cell.getContext());

  // For sub-row enter animations we need one painted frame at the closed state before
  // flipping to the open state, otherwise CSS has no prior style to interpolate from.
  // When `suppressEnterAnimation` is set we initialize as already-entered so the cell
  // paints open immediately.
  const [hasEntered, setHasEntered] = useState(suppressEnterAnimation);

  useLayoutEffect(() => {
    if (!animateVisibility || hasEntered) {
      return;
    }
    const timeoutId = setTimeout(() => setHasEntered(true), 0);
    return () => clearTimeout(timeoutId);
  }, [animateVisibility, hasEntered]);

  const showOpen = animateVisibility ? isVisible && hasEntered : isVisible;
  if (customCellProps?.rowSpan === 0) {
    return null;
  }

  return (
    <TableCell
      {...restCustomCellProps}
      className={cn(
        size === 'small' ? 'h-auto px-2 py-1' : undefined,
        getPinnedCellClassName(cell.column, 'body', canScrollLeft, canScrollRight),
        getColumnTextAlignmentClassName(cell.column),
        rowExpanded && 'bg-gray-100',
        isSortedColumn && 'bg-gray-100',
        'group-hover:bg-gray-100',
        animateVisibility && 'h-auto p-0',
        customClassName,
      )}
      style={{
        ...getPinnedCellStyles(cell.column, pinnedOffsets),
        ...customStyle,
      }}
    >
      {animateVisibility ? (
        <div
          style={{ transitionDuration: `${animationDurationMs}ms` }}
          className={cn(
            'grid transition-[grid-template-rows,opacity] ease-out',
            showOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
          )}
        >
          <div className="min-h-0 overflow-hidden">
            <div
              className={cn(
                'grid content-center whitespace-normal wrap-break-word',
                size === 'small'
                  ? 'h-auto px-2 py-1'
                  : [TABLE_CELL_HEIGHT_CLASS, TABLE_CELL_PADDING_CLASS],
              )}
            >
              {content}
            </div>
          </div>
        </div>
      ) : (
        content
      )}
    </TableCell>
  );
};

export default DataTableTableBodyCell;
